/**
 * Guard against the "reload loop" on the stale-chunk recovery screen.
 *
 * A tab opened across a release lazy-loads a chunk whose hashed filename is gone, and the
 * ErrorBoundary shows a "new version available" screen with a Reload button. Normally one
 * reload fixes it. Very occasionally it doesn't: a plain reload can be served a stale
 * index.html or a negatively-cached 404 for the new chunk (edge cache, or the deploy is
 * still mid-swap), so the tab lands straight back on the same screen while a fresh tab
 * works fine.
 *
 * The guard is deliberately tiny: stamp sessionStorage before reloading, and on the next
 * visit to the screen check whether a recent stamp exists. sessionStorage is per tab and
 * survives reloads, which is exactly the scope of the problem (a new tab starts clean).
 * The stamp expires by time rather than being cleared on boot, because a lazy import
 * fails after the entry module has already run, so there's no reliable "booted OK" hook
 * that fires before the failure.
 */

const STAMP_KEY = 'mx-chunk-reload';
const CACHE_BUST_PARAM = '_r';

/** How long after a reload attempt a repeat failure still counts as "stuck". */
const STUCK_WINDOW_MS = 5 * 60 * 1000;

/** True when this tab already reloaded from the recovery screen a few minutes ago. */
export function isRepeatChunkFailure(): boolean {
    try {
        const stamp = Number(sessionStorage.getItem(STAMP_KEY));
        return stamp > 0 && Date.now() - stamp < STUCK_WINDOW_MS;
    } catch {
        return false;
    }
}

/**
 * Reload the page, recording the attempt and bypassing any cached copy of the current URL
 * by appending a throwaway query parameter. `location.replace` (not `reload`) so the
 * cache-busted URL doesn't add a history entry.
 */
export function reloadBypassingCache(): void {
    try {
        sessionStorage.setItem(STAMP_KEY, String(Date.now()));
    } catch {
        // Storage unavailable (private mode / blocked). Still reload; we just lose the guard.
    }
    const url = new URL(window.location.href);
    url.searchParams.set(CACHE_BUST_PARAM, String(Date.now()));
    window.location.replace(url.toString());
}

/** The current URL without the cache-bust parameter, for "open in a new tab". */
export function currentUrlWithoutCacheBust(): string {
    const url = new URL(window.location.href);
    url.searchParams.delete(CACHE_BUST_PARAM);
    return url.toString();
}

/**
 * Remove the cache-bust parameter from the address bar once the app has started, so it
 * doesn't linger in bookmarks or copied links. Does not touch the sessionStorage stamp.
 */
export function stripCacheBustParamFromUrl(): void {
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(CACHE_BUST_PARAM)) {
            return;
        }
        url.searchParams.delete(CACHE_BUST_PARAM);
        window.history.replaceState(
            window.history.state,
            '',
            url.pathname + url.search + url.hash
        );
    } catch {
        // Cosmetic only; ignore.
    }
}

/** Test hook: make the next recovery screen render as a repeat failure. */
export function markChunkReloadAttemptForTest(): void {
    try {
        sessionStorage.setItem(STAMP_KEY, String(Date.now()));
    } catch {
        // ignore
    }
}
